'use client'

import { useState, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { CoreTab } from './right-panel/tabs/CoreTab'
import { ToolsTab } from './right-panel/tabs/ToolsTab'
import { MemTab } from './right-panel/tabs/MemTab'
import { ProjectAiTab } from './right-panel/tabs/ProjectAiTab'
import { FilesTab } from './right-panel/tabs/FilesTab'
import { ControlTab } from './right-panel/tabs/ControlTab'
import { TiCouneTab } from './right-panel/tabs/TiCouneTab'
import { usePageContext } from '../../stores/page-context.store'

// ─── Types ──────────────────────────────────────────────────────────────

type TaskStatus = 'TODO' | 'EN_COURS' | 'REVUE' | 'DONE'
type TaskPriority = 'HAUTE' | 'MOYENNE' | 'BASSE'

interface Task {
  id: string
  title: string
  status: TaskStatus
  priority: TaskPriority
  assignee?: string
  dueDate?: string
  tags: string[]
}

interface CalEvent {
  id: string
  title: string
  date: string
  color?: string
}

// ─── Icônes SVG ────────────────────────────────────────────────────────

const Icons = {
  Agent: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  Memory: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  Voice: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  ),
  Tools: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  Tasks: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  Calendar: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  Project: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  ),
  Close: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  ChevronLeft: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  ),
  ChevronRight: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  ),
  Plus: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
  Cpu: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
    </svg>
  ),
  Gpu: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
    </svg>
  ),
  MemoryChip: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  Folder: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
    </svg>
  ),
  Search: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  Brain: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  ),
}

// ─── Données statiques ──────────────────────────────────────────────

const AI_TABS = ['FICHIERS', 'CORE', 'TI-COUNE', 'OUTILS', 'MEM', 'VOICE'] as const
// « PROJET » n'existe que sur la page d'un projet, qui dépose son contexte (page-context.store).
// « FICHIERS » (explorateur + éditeur) fait partie du panneau IA — pas un panneau de plus — et est présent sur toutes les pages.
type AiTab = (typeof AI_TABS)[number] | 'PROJET' | 'FICHIERS' | 'CONTRÔLE'

// Sous-pages de /bureau qui ne sont PAS un espace de travail (/bureau/<slug>) : le panneau y reste le panneau IA.
const BUREAU_PAGES = new Set(['affaires', 'calendar', 'contacts', 'courriers', 'crm', 'documents', 'editeur', 'equipe', 'espaces', 'fichiers', 'files', 'join', 'kanban', 'modules', 'projects', 'projets', 'relations', 'remote', 'tasks', 'transformation'])

const WS_TABS = ['TÂCHES', 'CALENDRIER', 'PROJETS'] as const
type WsTab = (typeof WS_TABS)[number]

const STATUS_COLORS: Record<TaskStatus, string> = {
  TODO: 'var(--text-muted)',
  EN_COURS: 'var(--gold)',
  REVUE: 'var(--accent)',
  DONE: 'var(--teal)',
}

const STATUS_LABELS: Record<TaskStatus, string> = {
  TODO: 'À faire',
  EN_COURS: 'En cours',
  REVUE: 'Revue',
  DONE: 'Terminé',
}

const PRIO_COLORS: Record<TaskPriority, string> = {
  HAUTE: 'var(--coral)',
  MOYENNE: 'var(--gold)',
  BASSE: 'var(--text-muted)',
}

const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']

// ─── Styles ──────────────────────────────────────────────────────────

const styles = {
  panel: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    background: 'rgba(7,9,17,0.96)',
    fontFamily: 'var(--font-mono)',
    overflow: 'hidden',
    borderLeft: '1px solid rgba(255,255,255,0.04)',
  },
  tabBar: {
    display: 'flex',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    flexShrink: 0 as const,
    padding: '0 4px',
  },
  tabButton: (isActive: boolean) => ({
    flex: 1,
    padding: '10px 4px',
    background: 'none',
    border: 'none',
    borderBottom: isActive ? '2px solid var(--accent)' : '2px solid transparent',
    color: isActive ? 'var(--text-primary)' : 'rgba(255,255,255,0.25)',
    fontSize: 9,
    letterSpacing: '0.1em',
    fontFamily: 'var(--font-mono)',
    cursor: 'pointer',
    transition: 'color 0.15s, border-color 0.15s',
    fontWeight: isActive ? 600 : 400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  }),
  content: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '4px 14px 16px',
  },
  bureauHeader: {
    padding: '12px 14px 0',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    flexShrink: 0 as const,
  },
  bureauTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  bureauSlug: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.15)',
    fontFamily: 'var(--font-mono)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    flex: 1,
  },
  taskAdd: {
    display: 'flex',
    gap: 6,
    margin: '8px 0',
  },
  taskInput: {
    flex: 1,
    padding: '6px 10px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 6,
    color: 'var(--text-primary)',
    fontSize: 12,
    outline: 'none',
    fontFamily: 'var(--font-mono)',
  },
  taskItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 7,
    padding: '7px 0',
    borderBottom: '1px solid rgba(255,255,255,0.03)',
  },
  taskStatusBtn: (status: TaskStatus) => ({
    marginTop: 1,
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: `2px solid ${STATUS_COLORS[status]}`,
    background: status === 'DONE' ? STATUS_COLORS[status] : 'transparent',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'all 0.15s',
  }),
  taskTitle: (status: TaskStatus) => ({
    fontSize: 12,
    color: status === 'DONE' ? 'rgba(255,255,255,0.25)' : 'var(--text-secondary)',
    lineHeight: 1.4,
    textDecoration: status === 'DONE' ? 'line-through' : 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  }),
  calendarNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '8px 0 10px',
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 1,
    marginBottom: 10,
  },
  calendarDay: (isToday: boolean) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    fontSize: 11,
    fontFamily: 'var(--font-mono)',
    background: isToday ? 'var(--accent)' : 'transparent',
    color: isToday ? '#fff' : 'rgba(255,255,255,0.5)',
    fontWeight: isToday ? 700 : 400,
  }),
  projectItem: {
    marginBottom: 12,
  },
  projectProgress: {
    height: 4,
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 4,
    overflow: 'hidden' as const,
  },
  projectBar: (progress: number, color: string) => ({
    height: '100%',
    width: `${Math.min(progress, 100)}%`,
    background: color,
    borderRadius: 4,
    transition: 'width 0.5s ease',
  }),
  upcomingEvent: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 0',
    borderBottom: '1px solid rgba(255,255,255,0.03)',
  },
  progressBar: {
    height: 4,
    background: 'rgba(255,255,255,0.06)',
    borderRadius: 4,
    overflow: 'hidden' as const,
    marginTop: 6,
  },
  progressFill: (progress: number, color: string) => ({
    height: '100%',
    width: `${Math.min(progress, 100)}%`,
    background: color,
    borderRadius: 4,
    transition: 'width 0.5s ease',
  }),
  gpuBar: {
    height: 4,
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 4,
    overflow: 'hidden' as const,
  },
  gpuFill: (percent: number) => ({
    height: '100%',
    width: `${Math.min(percent, 100)}%`,
    background: 'linear-gradient(90deg, var(--accent), var(--teal))',
    borderRadius: 4,
    transition: 'width 0.5s ease',
  }),
}

// ─── Composants HUD ──────────────────────────────────────────────────

// ─── Onglets ──────────────────────────────────────────────────────────

function VoiceTab() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '60%',
      gap: 12,
    }}>
      <span style={{ fontSize: 48, opacity: 0.3 }}>🔊</span>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
        Aucun salon vocal actif
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'rgba(255,255,255,0.15)', textAlign: 'center', lineHeight: 1.7 }}>
        Rejoindre Conseil Municipal<br />ou Trading Room
      </div>
    </div>
  )
}

// ─── Bureau Panel ──────────────────────────────────────────────────────

function BureauPanel({ slug }: { slug: string }) {
  const [tab, setTab] = useState<WsTab>('TÂCHES')
  const [tasks, setTasks] = useState<Task[]>([])
  const [newTask, setNewTask] = useState('')
  const [addingTask, setAddingTask] = useState(false)
  const [events, setEvents] = useState<CalEvent[]>([])
  const [today] = useState(() => new Date())
  const [viewDate, setViewDate] = useState(() => new Date())

  const PROJECTS = [
    { name: 'Legal AI Engine', status: 'active', color: 'var(--accent)', progress: 72 },
    { name: 'Market Dashboard', status: 'active', color: 'var(--teal)', progress: 45 },
    { name: 'SmartMatch v2', status: 'planned', color: 'var(--gold)', progress: 10 },
    { name: 'Marketplace Core', status: 'active', color: '#f472b6', progress: 38 },
  ]

  useEffect(() => {
    try {
      const saved = localStorage.getItem('ws_tasks')
      if (saved) setTasks(JSON.parse(saved))
      else {
        setTasks([
          { id: '1', title: 'Configurer Qdrant mémoire sémantique', status: 'EN_COURS', priority: 'HAUTE', tags: ['infra'] },
          { id: '2', title: 'Migrations Prisma bureau', status: 'DONE', priority: 'HAUTE', tags: ['db'] },
          { id: '3', title: 'Documentation API', status: 'TODO', priority: 'BASSE', tags: ['docs'] },
        ])
      }
    } catch { /* ignore */ }
    try {
      const saved = localStorage.getItem('ws_events')
      if (saved) setEvents(JSON.parse(saved))
    } catch { /* ignore */ }
  }, [])

  function saveTasks(t: Task[]) {
    setTasks(t)
    try { localStorage.setItem('ws_tasks', JSON.stringify(t)) } catch { /* ignore */ }
  }

  function addTask() {
    if (!newTask.trim()) return
    const t: Task = { id: Date.now().toString(), title: newTask.trim(), status: 'TODO', priority: 'MOYENNE', tags: [] }
    saveTasks([...tasks, t])
    setNewTask('')
    setAddingTask(false)
  }

  function cycleStatus(id: string) {
    const order: TaskStatus[] = ['TODO', 'EN_COURS', 'REVUE', 'DONE']
    saveTasks(tasks.map(t => t.id !== id ? t : { ...t, status: order[(order.indexOf(t.status) + 1) % order.length] }))
  }

  function deleteTask(id: string) {
    saveTasks(tasks.filter(t => t.id !== id))
  }

  const year = viewDate.getFullYear()
  const month = viewDate.getMonth()
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

  function dayHasEvent(d: number) {
    const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    return events.some(e => e.date === ds)
  }

  const upcoming = events
    .filter(e => e.date >= todayStr)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 5)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={styles.bureauHeader}>
        <div style={styles.bureauTitle}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>BUREAU</span>
          <span style={styles.bureauSlug}>/{slug}</span>
        </div>
        <div style={{ display: 'flex', gap: 0 }}>
          {WS_TABS.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={styles.tabButton(t === tab)}
            >
              {t === 'TÂCHES' && <Icons.Tasks />}
              {t === 'CALENDRIER' && <Icons.Calendar />}
              {t === 'PROJETS' && <Icons.Project />}
              {t}
            </button>
          ))}
        </div>
      </div>

      <div style={styles.content}>
        {tab === 'TÂCHES' && (
          <>
            {addingTask ? (
              <div style={styles.taskAdd}>
                <input
                  autoFocus
                  value={newTask}
                  onChange={e => setNewTask(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addTask(); if (e.key === 'Escape') { setAddingTask(false); setNewTask('') } }}
                  placeholder="Nom de la tâche…"
                  style={styles.taskInput}
                />
                <button
                  onClick={addTask}
                  style={{
                    padding: '6px 10px',
                    background: 'var(--accent)',
                    border: 'none',
                    borderRadius: 6,
                    color: '#fff',
                    fontSize: 11,
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-dim)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--accent)'}
                >
                  <Icons.Plus /> Ajouter
                </button>
                <button
                  onClick={() => { setAddingTask(false); setNewTask('') }}
                  style={{
                    padding: '6px 8px',
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 6,
                    color: 'var(--text-muted)',
                    fontSize: 11,
                    cursor: 'pointer',
                  }}
                >
                  <Icons.Close />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setAddingTask(true)}
                style={{
                  width: '100%',
                  margin: '8px 0',
                  padding: '6px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px dashed rgba(255,255,255,0.06)',
                  borderRadius: 6,
                  color: 'rgba(255,255,255,0.2)',
                  fontSize: 11,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'var(--font-mono)',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(79,124,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.2)' }}
              >
                + Nouvelle tâche
              </button>
            )}

            {tasks.length === 0 ? (
              <div style={{ color: 'rgba(255,255,255,0.15)', fontSize: 11, textAlign: 'center', padding: '16px 0' }}>
                Aucune tâche
              </div>
            ) : (
              tasks.map(task => (
                <div key={task.id} style={styles.taskItem}>
                  <button
                    onClick={() => cycleStatus(task.id)}
                    title={STATUS_LABELS[task.status]}
                    style={styles.taskStatusBtn(task.status)}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.taskTitle(task.status)}>
                      {task.title}
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 9, color: PRIO_COLORS[task.priority], fontFamily: 'var(--font-mono)' }}>
                        {task.priority}
                      </span>
                      {task.dueDate && (
                        <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.15)', fontFamily: 'var(--font-mono)' }}>
                          {task.dueDate}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteTask(task.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255,255,255,0.15)',
                      cursor: 'pointer',
                      fontSize: 11,
                      flexShrink: 0,
                      transition: 'color 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--coral)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.15)'}
                  >
                    <Icons.Close />
                  </button>
                </div>
              ))
            )}

            {tasks.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                {(['TODO', 'EN_COURS', 'DONE'] as TaskStatus[]).map(s => {
                  const n = tasks.filter(t => t.status === s).length
                  if (!n) return null
                  return (
                    <div
                      key={s}
                      style={{
                        padding: '3px 8px',
                        borderRadius: 4,
                        background: `${STATUS_COLORS[s]}12`,
                        border: `1px solid ${STATUS_COLORS[s]}25`,
                        fontSize: 10,
                        color: STATUS_COLORS[s],
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {n} {STATUS_LABELS[s].toLowerCase()}
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}

        {tab === 'CALENDRIER' && (
          <>
            <div style={styles.calendarNav}>
              <button
                onClick={() => setViewDate(new Date(year, month - 1))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.2)',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: 4,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <Icons.ChevronLeft />
              </button>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
                {MONTHS_FR[month]} {year}
              </span>
              <button
                onClick={() => setViewDate(new Date(year, month + 1))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.2)',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: 4,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <Icons.ChevronRight />
              </button>
            </div>

            <div style={styles.calendarGrid}>
              {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((d, i) => (
                <div key={i} style={{ fontSize: 9, color: 'rgba(255,255,255,0.15)', fontFamily: 'var(--font-mono)', textAlign: 'center', padding: '2px 0' }}>
                  {d}
                </div>
              ))}
              {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const d = i + 1
                const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
                const isToday = ds === todayStr
                const hasEv = dayHasEvent(d)
                return (
                  <div key={d} style={{ position: 'relative', textAlign: 'center', padding: '3px 0', cursor: 'default' }}>
                    <span style={styles.calendarDay(isToday)}>{d}</span>
                    {hasEv && (
                      <span style={{
                        position: 'absolute',
                        bottom: 1,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        background: 'var(--accent)',
                      }} />
                    )}
                  </div>
                )
              })}
            </div>

            {upcoming.length > 0 && (
              <>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.15)', fontFamily: 'var(--font-mono)', letterSpacing: '0.15em', marginBottom: 8, textTransform: 'uppercase' }}>
                  À VENIR
                </div>
                {upcoming.map(ev => (
                  <div key={ev.id} style={styles.upcomingEvent}>
                    <span style={{ width: 6, height: 6, borderRadius: 2, background: ev.color ?? 'var(--accent)', flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 11, color: 'rgba(255,255,255,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev.title}
                    </span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.15)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                      {ev.date.slice(5)}
                    </span>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {tab === 'PROJETS' && (
          <>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.15)', fontFamily: 'var(--font-mono)', letterSpacing: '0.15em', margin: '12px 0 10px', textTransform: 'uppercase' }}>
              PROJETS ACTIFS
            </div>
            {PROJECTS.map((p, i) => (
              <div key={i} style={styles.projectItem}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{p.name}</span>
                  <span style={{ fontSize: 10, color: p.status === 'active' ? p.color : 'rgba(255,255,255,0.15)', fontFamily: 'var(--font-mono)' }}>
                    {p.progress}%
                  </span>
                </div>
                <div style={styles.projectProgress}>
                  <div style={styles.projectBar(p.progress, p.color)} />
                </div>
              </div>
            ))}
            <a
              href="/bureau/projects"
              style={{
                display: 'block',
                marginTop: 10,
                padding: '7px',
                background: 'rgba(79,124,255,0.04)',
                border: '1px solid rgba(79,124,255,0.1)',
                borderRadius: 7,
                color: 'var(--accent)',
                fontSize: 11,
                textAlign: 'center',
                textDecoration: 'none',
                fontFamily: 'var(--font-mono)',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(79,124,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(79,124,255,0.2)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(79,124,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(79,124,255,0.1)' }}
            >
              Voir tous les projets →
            </a>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Panneau principal ──────────────────────────────────────────────────

export default function RightPanel() {
  const pathname = usePathname()
  const wsMatch = pathname?.match(/^\/bureau\/([^/]+)$/)
  const wsSlug = wsMatch && !BUREAU_PAGES.has(wsMatch[1]) ? wsMatch[1] : null
  const [aiTab, setAiTab] = useState<AiTab>('FICHIERS')
  const projectId = usePageContext(s => s.project?.facts.projectId ?? null)
  const documentId = usePageContext(s => s.document?.fileId ?? null)
  // Arriver sur la page d'un projet ouvre l'onglet qui en parle ; la quitter ramène à CORE.
  useEffect(() => { setAiTab(projectId ? 'PROJET' : t => (t === 'PROJET' ? 'FICHIERS' : t)) }, [projectId])
  // Ouvrir « Historique et questions » d'un document ouvre l'onglet où se pose la question.
  useEffect(() => { if (documentId) setAiTab('TI-COUNE') }, [documentId])
  // Pages d'administration des organisations : l'onglet CONTRÔLE (contexte d'organisation) précède l'explorateur, dans le MÊME panneau.
  const adminOrgs = !!pathname && /^\/admin\/(organisations|membres)(\/|$)/.test(pathname)
  useEffect(() => { if (adminOrgs) setAiTab('CONTRÔLE') }, [adminOrgs])
  const base: readonly AiTab[] = adminOrgs ? ['CONTRÔLE', ...AI_TABS] : AI_TABS
  const tabs: readonly AiTab[] = projectId ? ['PROJET', ...base] : base
  const current: AiTab = tabs.includes(aiTab) ? aiTab : tabs[0]

  if (wsSlug) {
    return (
      <div style={styles.panel}>
        <BureauPanel slug={wsSlug} />
        <style>{`
          @keyframes pulse-dot {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.4; transform: scale(0.8); }
          }
          @keyframes fadeInTrace {
            from { opacity: 0; transform: translateX(-8px); }
            to { opacity: 1; transform: translateX(0); }
          }
        `}</style>
      </div>
    )
  }

  return (
    <div style={styles.panel}>
      <div style={styles.tabBar}>
        {tabs.map((t) => {
          const isActive = t === current
          const iconMap = {
            PROJET: <Icons.Brain />,
            FICHIERS: <Icons.Folder />,
            'CONTRÔLE': <Icons.Tools />,
            'TI-COUNE': <Icons.Brain />,
            CORE: <Icons.Agent />,
            OUTILS: <Icons.Tools />,
            MEM: <Icons.Memory />,
            VOICE: <Icons.Voice />,
          }
          return (
            <button
              key={t}
              onClick={() => setAiTab(t)}
              style={styles.tabButton(isActive)}
            >
              {iconMap[t]}
              {t}
            </button>
          )
        })}
      </div>

      <div style={styles.content}>
        {current === 'PROJET' && <ProjectAiTab />}
        {current === 'CONTRÔLE' && <ControlTab />}
        {current === 'FICHIERS' && <FilesTab />}
        {current === 'CORE' && <CoreTab />}
        {current === 'TI-COUNE' && <TiCouneTab />}
        {current === 'OUTILS' && <ToolsTab />}
        {current === 'MEM' && <MemTab />}
        {current === 'VOICE' && <VoiceTab />}
      </div>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.8); }
        }
        @keyframes fadeInTrace {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  )
}